<template>
  <ModalMask v-if="show" :inline="inline" @click="onClickOutside">
    <div class="dialog" :key="title">
      <div class="main">
        <h1 v-if="title">{{ title }}</h1>
        <div v-if="message || target" class="modal-main">
          <div class="detail">
            <p v-if="message">{{ message }}</p>
            <aside v-if="target">{{ target }}</aside>
            <p
              v-if="description"
              class="description"
              :style="[{ 'font-size': `${descriptionSize}px` }, { 'text-align': descriptionPosition }]"
            >
              {{ description }}
            </p>
          </div>
        </div>
      </div>
      <footer class="footer">
        <Button v-if="confirm" @click="onCancel">{{ cancelText || 'いいえ' }}</Button>
        <Button :primary="!caution && confirm" :caution="caution" @click="onOk">
          {{ okText || (confirm ? 'はい' : '戻る') }}
        </Button>
      </footer>
    </div>
  </ModalMask>
</template>

<script>
import { pull } from 'lodash'
import Vue from 'vue'
import pageTitleMap from '@/resources/pageTitleMap'
import ModalMask from '@/components/molecules/ModalMask'
import Button from '@/components/atoms/Button'

const opens = []

/**
 * モーダルダイアログ
 *
 * show と confirm 関数を用意しています。
 */
export default {
  name: 'ModalDialog',
  components: {
    ModalMask,
    Button,
  },
  props: {
    /** タイトル */
    title: { type: String, default: null },
    /** メッセージ */
    message: { type: String, default: null },
    /** 対象名 */
    target: { type: String, default: null },
    /** 補足文 */
    description: { type: String, default: null },
    /** 補足文のfont-size*/
    descriptionSize: { type: Number, default: 11 },
    /** 補足文の位置*/
    descriptionPosition: {
      type: String,
      default: 'center',
      validator(value) {
        return (Array.isArray(value) ? value : value.split(' ')).every(val => ['left', 'center', 'right'].includes(val))
      },
    },
    /** 注意 */
    caution: { type: Boolean, default: false },
    /** 確認ダイアログ */
    confirm: { type: Boolean, default: false },
    /** キャンセルボタンのテキスト */
    cancelText: { type: String, default: null },
    /** OKボタンのテキスト */
    okText: { type: String, default: null },
    /**
     * インライン表示（スタイルガイド用）
     * @ignore
     */
    inline: { type: Boolean, default: false },
  },
  data() {
    return {
      show: true,
    }
  },
  methods: {
    onOk() {
      /**
       * OKボタンクリック
       * @event
       */
      this.$emit('ok')
      this.close()
    },
    onCancel() {
      /**
       * キャンセルボタンクリック
       * @event
       */
      this.$emit('cancel')
      this.close()
    },
    onClickOutside() {
      /**
       * ダイアログ外クリック
       * @event
       */
      this.$emit('clickOutside')
      this.close()
    },
    close() {
      pull(opens, this)
      /**
       * クローズ
       * @event
       */
      this.$emit('close')
      this.$el.remove()
      this.$destroy()
    },
  },

  closeAll() {
    Array.from(opens).forEach(dialog => dialog.close())
  },

  /**
   * ダイアログを表示し、確認完了 Promise を返します
   *
   * @param {object} props Dialog の props
   * @returns {Promise} 確認完了の Promise
   */
  show(props) {
    return new Promise(resolve => {
      const Component = Vue.extend(this)
      const dialog = new Component({ propsData: { ...props, confirm: false } })
        .$on(['ok', 'clickOutside'], () => resolve(true))
        .$mount()
      opens.push(dialog)
      document.body.appendChild(dialog.$el)
    })
  },

  /**
   * 確認ダイアログを表示し、確認完了 Promise を返します
   *
   * @param {object} props Dialog の props
   * @returns {Promise} 確認完了の Promise
   */
  confirm(props) {
    return new Promise(resolve => {
      const Component = Vue.extend(this)
      const dialog = new Component({ propsData: { ...props, confirm: true } })
        .$on('ok', () => resolve(true))
        .$on(['cancel', 'clickOutside'], () => resolve(false))
        .$mount()
      opens.push(dialog)
      document.body.appendChild(dialog.$el)
    })
  },

  /**
   * 設定画面で編集をしていたら確認ダイアログを表示し、確認完了 Promise を返します
   *
   * @param {object} url ページURL
   * @returns {Promise} 確認完了の Promise
   */
  confirmLeaveSetting(url) {
    const path = url.replace(/\/$|[¥/]{0,}\?.*$|[¥/][0-9]{1,}[¥/]{0,}$/, '')
    const title = pageTitleMap[path] ? pageTitleMap[path] : '設定画面'
    return this.confirm({
      title,
      message: '変更を中止してよろしいでしょうか？',
      okText: '中止する',
      cancelText: '戻る',
      caution: true,
    })
  },

  /**
   * 顧客台帳などで編集をしていたら確認ダイアログを表示し、確認完了 Promise を返します
   *
   * @returns {Promise} 確認完了の Promise
   */
  confirmDispose() {
    return this.confirm({
      title: '確認',
      message: '登録中のデータを\n破棄してもよろしいですか？',
      okText: 'はい',
      cancelText: 'キャンセル',
      caution: true,
    })
  },
  /**
   * 予約登録、編集画面でキャンセルまたは画面から離れる際に確認ダイアログを表示し、確認完了 Promise を返します
   *
   * @returns {Promise} 確認完了の Promise
   */
  confirmLeaveReserve() {
    return this.confirm({
      title: '予約は完了していません',
      message: '予約の登録を中止してよろしいですか？',
      okText: '中止する',
      cancelText: '戻る',
      caution: true,
    })
  },
}
</script>

<style lang="postcss" scoped>
.dialog {
  background: var(--white);
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  box-sizing: border-box;
  display: grid;
  grid-gap: 32px;
  grid-template-rows: 1fr max-content;
  justify-items: center;
  line-height: 24px;
  max-height: calc(100% - 64px);
  min-height: 224px;
  padding: 32px;
  text-align: center;
  transition: all 0.3s ease;
  width: 480px;

  .responsive & {
    @media screen and (--sp-dialog) {
      width: calc(100% - 32px);
    }
  }
}

.main {
  display: grid;
  grid-gap: 24px;
  overflow: hidden;
  white-space: pre-line;

  .responsive & {
    @media screen and (--sp-modal) {
      align-content: center;
    }
  }
}

.modal-main {
  display: grid;
  grid-gap: 24px;
  overflow-y: scroll;

  &::-webkit-scrollbar {
    display: none;
  }

  .responsive & {
    @media screen and (--sp-dialog) {
      width: 100%;
    }
  }
}

h1 {
  align-self: center;
  font-size: 21px;

  .responsive & {
    @media screen and (--sp-modal) {
      font-size: 16px;
    }
  }
}

.detail {
  display: grid;
  grid-gap: 8px;
  white-space: pre-line;
}

aside {
  font-size: 15px;
  font-weight: 600;
}

.description {
  line-height: 1.4;
}

.footer {
  display: grid;
  grid-auto-flow: column;
  grid-gap: 32px;

  .responsive & {
    @media screen and (--sp-modal) {
      grid-auto-columns: 1fr;
      grid-gap: 16px;
      width: 100%;

      button {
        border-radius: 4px;
        font-size: 15px;
        height: 40px;
        line-height: normal;
        width: 100%;
      }
    }
  }
}
</style>
